import { RULE_CODES } from "./codes";
import { postalCodeIssue, US_ZIP_PATTERN } from "./country-meta";
import type { AddressInput, RuleViolation } from "./types";
import { isValidUSState } from "./us-states";

const MAX_STREET_LENGTH = 255;
const MIN_STREET_LENGTH = 3;
const MAX_CITY_LENGTH = 100;
const MAX_STATE_LENGTH = 50;

export const STREET_TOO_SHORT_MESSAGE =
  "Street address must be at least 3 characters and include a letter or number";

/** Any letter or digit, in any script: street names are not held to V5. */
const STREET_CONTENT = new RegExp(String.raw`[\p{L}\p{N}]`, "u");

/** US ZIP: 12345 or 12345-6789. */
export function isValidUSZip(value: string): boolean {
  if (typeof value !== "string") return false;
  return US_ZIP_PATTERN.test(value.trim());
}

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** At least three characters, one of them a letter or digit (V3). */
export function isUsableStreet(street: string | null | undefined): boolean {
  const value = text(street);
  return (
    value !== null &&
    value.length >= MIN_STREET_LENGTH &&
    STREET_CONTENT.test(value)
  );
}

function field(index: number, suffix: string): string {
  return `addresses[${index}].${suffix}`;
}

export interface AddressFieldOptions {
  /**
   * A person's own address (a shareholder's home address). Outside the US many
   * addresses have no street line (rural areas, Gulf states, building and
   * district addresses) and many countries have no state or province, so only
   * the city, the country and the postal code the country uses are required
   * there. A US address keeps every rule. Company addresses never set this.
   */
  readonly personal?: boolean;
}

/** True for a US address, and for one with no country (judged as US). */
export function isUsAddressCountry(
  country: string | null | undefined,
): boolean {
  const value = text(country);
  return value === null || value.toUpperCase() === "US";
}

/**
 * Street, city, country, state and postal code — the part of an address that
 * is the same for a company address and a shareholder's home address. `path`
 * turns a field name into the violation's UI path.
 */
export function validateAddressFields(
  address: AddressInput | null | undefined,
  path: (suffix: string) => string,
  options: AddressFieldOptions = {},
): RuleViolation[] {
  const source: AddressInput = address ?? {};
  const violations: RuleViolation[] = [];
  const country = text(source.country);
  const lenient = options.personal === true && !isUsAddressCountry(country);

  const street = text(source.street);
  if (street === null && lenient) {
    // Outside the US a person's address may have no street line.
  } else if (!street || street.length > MAX_STREET_LENGTH) {
    violations.push({
      code: RULE_CODES.ADDRESS_STREET_REQUIRED,
      field: path("street"),
      message: "Street address is required",
    });
  } else if (!isUsableStreet(street)) {
    violations.push({
      code: RULE_CODES.ADDRESS_STREET_INVALID,
      field: path("street"),
      message: STREET_TOO_SHORT_MESSAGE,
    });
  }

  const city = text(source.city);
  if (!city || city.length > MAX_CITY_LENGTH) {
    violations.push({
      code: RULE_CODES.ADDRESS_CITY_REQUIRED,
      field: path("city"),
      message: "City is required",
    });
  }

  if (!country) {
    violations.push({
      code: RULE_CODES.ADDRESS_COUNTRY_REQUIRED,
      field: path("country"),
      message: "Country is required",
    });
  }

  const state = text(source.state);
  if (state === null && lenient) {
    // Outside the US a person's address may have no state or province.
  } else if (!state || state.length > MAX_STATE_LENGTH) {
    violations.push({
      code: RULE_CODES.ADDRESS_STATE_REQUIRED,
      field: path("state"),
      message: "State/Province is required",
    });
  } else if (country?.toUpperCase() === "US" && !isValidUSState(state)) {
    violations.push({
      code: RULE_CODES.ADDRESS_STATE_INVALID,
      field: path("state"),
      message: "US state must be a valid 2-letter code (e.g., CA, NY)",
    });
  }

  const zipCode = text(source.zipCode);
  const postal = postalCodeIssue(country, zipCode);
  if (postal !== null) {
    violations.push({
      code: zipCode
        ? RULE_CODES.ADDRESS_ZIP_INVALID
        : RULE_CODES.ADDRESS_ZIP_REQUIRED,
      field: path("zipCode"),
      message: postal,
    });
  }

  return violations;
}

function validateAddress(
  address: AddressInput,
  index: number,
): RuleViolation[] {
  const violations: RuleViolation[] = [];
  const kind = text(address.type)?.toLowerCase();

  if (kind !== "primary" && kind !== "mailing") {
    violations.push({
      code: RULE_CODES.ADDRESS_TYPE_INVALID,
      field: field(index, "type"),
      message: "Address type must be primary or mailing",
    });
  }

  violations.push(
    ...validateAddressFields(address, (suffix) => field(index, suffix)),
  );
  return violations;
}

/**
 * Address-set rules: exactly one primary, at most one mailing, and each entry
 * complete enough to print on a filing.
 */
export function validateAddresses(addresses: AddressInput[]): RuleViolation[] {
  if (!Array.isArray(addresses) || addresses.length === 0) {
    return [
      {
        code: RULE_CODES.ADDRESSES_REQUIRED,
        field: "addresses",
        message: "At least one address is required",
      },
    ];
  }

  const violations: RuleViolation[] = [];

  addresses.forEach((address, index) => {
    violations.push(...validateAddress(address ?? {}, index));
  });

  const kinds = addresses.map((address) => text(address?.type)?.toLowerCase());
  const primaryCount = kinds.filter((kind) => kind === "primary").length;
  const mailingCount = kinds.filter((kind) => kind === "mailing").length;

  if (primaryCount !== 1) {
    violations.push({
      code: RULE_CODES.ADDRESS_PRIMARY_REQUIRED,
      field: "addresses",
      message: "Exactly one primary address is required",
    });
  }

  if (mailingCount > 1) {
    violations.push({
      code: RULE_CODES.ADDRESS_MAILING_DUPLICATE,
      field: "addresses",
      message: "At most one mailing address is allowed",
    });
  }

  return violations;
}
